import React from 'react';
import { TrendingUp, TrendingDown, Users, FileText, Clock, Edit3 } from "lucide-react";
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import userService from '../../services/userService';
import blogService from '../../services/blogService';

export function EnhancedSectionCards() {
  const { currentColors } = useAdminTheme();
  
  // Get real data from your services
  const userStats = userService.getUserStats();
  const blogStats = blogService.getBlogStats();

  const cards = [
    {
      title: "Total Revenue",
      value: "$15,250.00",
      change: "+12.5%",
      trend: "up",
      description: "Total Revenue",
      subtitle: "Trending up this month",
      footer: "Revenue for the last 6 months",
      icon: TrendingUp,
      color: currentColors.primary
    },
    {
      title: "New Users",
      value: userStats.total.toString(),
      change: "+5%",
      trend: "up", 
      description: "New Users",
      subtitle: "Active registered users",
      footer: "User acquisition steady",
      icon: Users,
      color: currentColors.success
    },
    {
      title: "Pending Approvals",
      value: userStats.pending.toString(),
      change: "+8%",
      trend: "up",
      description: "Pending Approvals", 
      subtitle: "Users awaiting approval",
      footer: "Review queue growing",
      icon: Clock,
      color: currentColors.warning
    },
    {
      title: "Published Content",
      value: blogStats.published.toString(),
      change: "+15%",
      trend: "up",
      description: "Published Content",
      subtitle: "Live blog posts",
      footer: "Content creation active",
      icon: FileText,
      color: currentColors.info
    }
  ];

  return (
    <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card, index) => {
        const IconComponent = card.icon;
        const isPositive = card.trend === "up";
        const TrendIcon = isPositive ? TrendingUp : TrendingDown;
        
        return (
          <Card key={index} className="relative overflow-hidden group hover:shadow-lg transition-all duration-200">
            {/* Gradient background */}
            <div 
              className="absolute inset-0 opacity-5"
              style={{
                background: `linear-gradient(135deg, ${card.color} 0%, transparent 100%)`
              }}
            />
            
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription className="text-sm font-medium">
                  {card.description}
                </CardDescription>
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${card.color}15` }}
                >
                  <IconComponent 
                    className="h-4 w-4" 
                    style={{ color: card.color }}
                  />
                </div>
              </div>
              
              <CardTitle className="text-2xl font-bold tabular-nums lg:text-3xl">
                {card.value}
              </CardTitle>
              
              <div className="flex items-center">
                <Badge 
                  variant="outline" 
                  className="flex items-center gap-1 text-xs font-medium border-0"
                  style={{ 
                    backgroundColor: `${isPositive ? currentColors.success : currentColors.error}15`,
                    color: isPositive ? currentColors.success : currentColors.error
                  }}
                >
                  <TrendIcon className="h-3 w-3" />
                  {card.change}
                </Badge>
              </div>
            </CardHeader>
            
            <CardFooter className="pt-0">
              <div className="flex flex-col gap-1 text-sm">
                <div className="flex items-center gap-2 font-medium">
                  {card.subtitle}
                  <TrendIcon 
                    className="h-4 w-4" 
                    style={{ color: isPositive ? currentColors.success : currentColors.error }}
                  />
                </div>
                <div 
                  className="text-xs"
                  style={{ color: currentColors.textMuted }}
                >
                  {card.footer}
                </div>
              </div>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}

